import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useBoxes } from '@/lib/api/boxes';
import { useLiveIps } from '@/lib/api/liveBoxes';
import { TaskCommand, createTask } from '@/lib/api/task';
import { boxActions } from '@/pages/boxes-list/box-actions';
import { sendCcCommand } from '@/lib/api/remoteCommands';
import { Action } from '@/pages/boxes-list/action-button';
import {
  FILTERS,
  FilterKey,
  TvStatus,
  stadiumIpNameIndex,
  statusFromBox,
} from './data';
import { StadiumSceneHandle, TvTip, createStadiumScene } from './scene';

// Live TV map — 3D stadium view of every monitored TV, colored by the box
// status the agent writes to Firestore. Default landing page for users with
// the `boxes` claim.

const STATUS_LABELS: Record<TvStatus, string> = {
  online: 'ONLINE',
  degraded: 'CMD FAIL', // pings but the dummy command failed
  offline: 'OFFLINE',
  unknown: 'NO DATA',
};

const STATUS_TEXT_CLASS: Record<TvStatus, string> = {
  online: 'text-[#00c8d2]',
  degraded: 'text-[#ffb454]',
  offline: 'text-[#ff4d4d]',
  unknown: 'text-[#7d95a3]',
};

const STATUS_DOT_CLASS: Record<TvStatus, string> = {
  online: 'bg-[#00c8d2]',
  degraded: 'bg-[#ffb454]',
  offline: 'bg-[#ff4d4d]',
  unknown: 'bg-[#5a6a74]',
};

const PANEL_CLASS =
  'pointer-events-auto rounded-md border border-[#1e2e38] bg-[#101a21]/90 backdrop-blur';

export function StadiumMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const tipElRef = useRef<HTMLDivElement>(null);
  const [handle, setHandle] = useState<StadiumSceneHandle | null>(null);
  const [filter, setFilter] = useState<FilterKey>('ALL');
  const [tip, setTip] = useState<TvTip | null>(null);
  const [menu, setMenu] = useState<{ tip: TvTip; x: number; y: number } | null>(
    null
  );
  const [boxes] = useBoxes();
  const liveIps = useLiveIps();
  const navigate = useNavigate();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const scene = createStadiumScene(el, {
      onHover: (t, x, y) => {
        if (t && tipElRef.current) {
          tipElRef.current.style.left =
            Math.min(x + 14, window.innerWidth - 240) + 'px';
          tipElRef.current.style.top = Math.max(y - 14, 74) + 'px';
        }
        // Tips are precomputed per TV, so identity comparison avoids a
        // re-render on every pointermove while the same TV stays hovered.
        setTip((prev) => (prev === t ? prev : t));
      },
      onSelect: (t, x, y) => {
        setMenu(
          t
            ? {
                tip: t,
                x: Math.min(x, window.innerWidth - 230),
                y: Math.min(Math.max(y, 74), window.innerHeight - 420),
              }
            : null
        );
      },
    });
    setHandle(scene);
    return () => {
      setHandle(null);
      scene.dispose();
    };
  }, []);

  // The scene bakes the stadium-data.json ip into every TV. Boxes are looked
  // up BY NAME (the stable, unique key) so an IP edited in the dashboard
  // doesn't orphan its TV; direct ip lookup is only a fallback for renames.
  const ipToName = useMemo(() => stadiumIpNameIndex(), []);
  const resolveBox = useMemo(() => {
    const byName = new Map((boxes ?? []).map((b) => [b.name, b]));
    const byIp = new Map((boxes ?? []).map((b) => [b.ip, b]));
    return (jsonIp: string) =>
      byName.get(ipToName.get(jsonIp) ?? '') ?? byIp.get(jsonIp);
  }, [boxes, ipToName]);

  /** Current address of a TV's box (the JSON ip may be stale after an edit). */
  const liveIp = (jsonIp: string) => resolveBox(jsonIp)?.ip ?? jsonIp;

  // CC toggles are ProductionCommands FIELD_ROW writes keyed by box NAME
  // (the box app acks by deleting the doc) — not agent tasks, so no task
  // page to navigate to and no live-view block (nothing touches adb).
  const runCc = (on: boolean, jsonIp: string) => {
    setMenu(null);
    const name = resolveBox(jsonIp)?.name ?? ipToName.get(jsonIp);
    if (!name) {
      toast.error('No box on file for this TV');
      return;
    }
    sendCcCommand([name], on)
      .then(() => toast.success(`CC ${on ? 'on' : 'off'} sent to ${name}`))
      .catch((e) => {
        console.error('cc command failed', e);
        toast.error('Failed to send CC command');
      });
  };

  // Same behavior as the Boxes list: commands are blocked for boxes that are
  // in live view (a command would adb-disconnect and kill the scrcpy session).
  const runAction = (action: Action, jsonIp: string) => {
    setMenu(null);
    const ip = liveIp(jsonIp);
    if (liveIps.has(ip)) {
      toast.error('Box is in live view — commands blocked');
      return;
    }
    createTask({
      boxes: [ip],
      command: action.value as TaskCommand,
      isTvControllerTask: false,
    }).then((id) => navigate(`/boxes/task/${id}`));
  };

  // Keyed by the JSON ip the scene holds, valued from the name-resolved box.
  const statusByIp = useMemo(() => {
    const m = new Map<string, TvStatus>();
    for (const jsonIp of ipToName.keys()) {
      const box = resolveBox(jsonIp);
      if (box) m.set(jsonIp, statusFromBox(box.status));
    }
    // Boxes not in the stadium data still register under their own ip so any
    // TV whose JSON ip matches directly keeps working.
    boxes?.forEach((b) => {
      if (!ipToName.has(b.ip)) m.set(b.ip, statusFromBox(b.status));
    });
    return m;
  }, [boxes, ipToName, resolveBox]);

  useEffect(() => {
    handle?.updateStatuses(statusByIp);
  }, [handle, statusByIp]);

  useEffect(() => {
    handle?.setFilter(filter);
  }, [handle, filter]);

  const counts = useMemo(() => {
    const bySec: Record<string, number> = {
      '147': 0,
      '145': 0,
      NINES: 0,
      SMR: 0,
      EZ: 0,
    };
    const byStatus: Record<TvStatus, number> = {
      online: 0,
      degraded: 0,
      offline: 0,
      unknown: 0,
    };
    for (const tv of handle?.tvs ?? []) {
      bySec[tv.sec] += 1;
      const status = tv.ip ? (statusByIp.get(tv.ip) ?? 'unknown') : 'unknown';
      byStatus[status] += 1;
    }
    return { bySec, byStatus, total: handle?.tvs.length ?? 0 };
  }, [handle, statusByIp]);

  const tipStatus: TvStatus = tip?.ip
    ? (statusByIp.get(tip.ip) ?? 'unknown')
    : 'unknown';

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 overflow-hidden bg-[#0b1116] font-mono text-xs text-[#dce8ee]">
      <div ref={containerRef} className="absolute inset-0 cursor-grab" />

      {/* title */}
      <div
        className={`absolute left-3 top-3 max-w-[300px] px-4 py-3 ${PANEL_CLASS}`}
      >
        <h1 className="text-[13px] font-semibold tracking-[.14em] text-[#00c8d2]">
          LRB 147·145 <span className="text-[#fc6a1e]">+ NINES + SMR</span>
        </h1>
        <p className="mt-1 hidden text-[10.5px] tracking-[.04em] text-[#7d95a3] sm:block">
          live status · refreshed by the monitoring agent
        </p>
      </div>

      {/* status + section stats */}
      <div
        className={`absolute right-3 top-3 flex flex-col gap-2 px-3.5 py-2.5 ${PANEL_CLASS}`}
      >
        <div className="flex gap-4">
          {(['online', 'degraded', 'offline', 'unknown'] as TvStatus[]).map(
            (s) => (
              <div key={s} className="text-right">
                <b
                  className={`block text-xl font-semibold leading-tight tabular-nums ${STATUS_TEXT_CLASS[s]}`}
                >
                  {counts.byStatus[s]}
                </b>
                <i className="text-[9px] not-italic tracking-[.12em] text-[#7d95a3]">
                  {STATUS_LABELS[s]}
                </i>
              </div>
            )
          )}
          <div className="text-right">
            <b className="block text-xl font-semibold leading-tight tabular-nums text-[#fc6a1e]">
              {counts.total}
            </b>
            <i className="text-[9px] not-italic tracking-[.12em] text-[#7d95a3]">
              TOTAL TVS
            </i>
          </div>
        </div>
        <div className="hidden justify-end gap-3 text-[9px] tracking-[.1em] text-[#7d95a3] md:flex">
          <span>147 · {counts.bySec['147']}</span>
          <span>145 · {counts.bySec['145']}</span>
          <span>NINES · {counts.bySec.NINES}</span>
          <span>SMR · {counts.bySec.SMR}</span>
          <span>FIELD · {counts.bySec.EZ}</span>
        </div>
      </div>

      {/* filters */}
      <div
        className={`absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 p-1.5 ${PANEL_CLASS}`}
      >
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded border px-3 py-1.5 text-[10px] tracking-[.1em] transition-colors ${
              filter === f
                ? 'border-[#00c8d2] bg-[#00c8d2] font-semibold text-[#06181c]'
                : 'border-[#1e2e38] text-[#7d95a3] hover:border-[#7d95a3] hover:text-[#dce8ee]'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* legend */}
      <div
        className={`absolute bottom-3 left-3 hidden gap-3.5 px-3 py-2 text-[10px] text-[#7d95a3] md:flex ${PANEL_CLASS}`}
      >
        {(['online', 'degraded', 'offline', 'unknown'] as TvStatus[]).map(
          (s) => (
            <span key={s} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-sm ${STATUS_DOT_CLASS[s]}`} />
              {STATUS_LABELS[s]}
            </span>
          )
        )}
      </div>

      {/* hint */}
      <div
        className={`absolute bottom-3 right-3 hidden px-2.5 py-1.5 text-[10px] tracking-[.05em] text-[#7d95a3] md:block ${PANEL_CLASS}`}
      >
        drag rotate · shift-drag / 2-finger pan · scroll zoom
      </div>

      {/* click menu — same actions as the Boxes list */}
      {menu && (
        <div
          className="fixed z-20 min-w-[210px] rounded border border-[#1e2e38] bg-[#0d151b]/95 py-1.5 text-[11px] shadow-xl backdrop-blur"
          style={{ left: menu.x, top: menu.y }}
        >
          <div className="px-3 pb-1.5 pt-1">
            <div className="font-semibold text-[#00c8d2]">{menu.tip.title}</div>
            {menu.tip.lines.map((line, i) => (
              <div key={i} className="mt-0.5 text-[10px] text-[#7d95a3]">
                {line}
              </div>
            ))}
          </div>
          {menu.tip.ip && liveIp(menu.tip.ip) !== menu.tip.ip && (
            <div className="px-3 pb-1 text-[10px] text-[#ffb454]">
              now at {liveIp(menu.tip.ip)}
            </div>
          )}
          {menu.tip.ip ? (
            <div className="border-t border-[#1e2e38] pt-1">
              {boxActions.map((action) => (
                <button
                  key={action.value}
                  onClick={() => runAction(action, menu.tip.ip!)}
                  className="block w-full px-3 py-1.5 text-left text-[#dce8ee] transition-colors hover:bg-[#1e2e38] hover:text-[#00c8d2]"
                >
                  {action.label}
                </button>
              ))}
              <div className="my-1 border-t border-[#1e2e38]"></div>
              {[
                { label: 'CC On', on: true },
                { label: 'CC Off', on: false },
              ].map((cc) => (
                <button
                  key={cc.label}
                  onClick={() => runCc(cc.on, menu.tip.ip!)}
                  className="block w-full px-3 py-1.5 text-left text-[#dce8ee] transition-colors hover:bg-[#1e2e38] hover:text-[#00c8d2]"
                >
                  {cc.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="border-t border-[#1e2e38] px-3 py-1.5 text-[10px] text-[#fc6a1e]">
              no device on file — no actions
            </div>
          )}
        </div>
      )}

      {/* tooltip */}
      <div
        ref={tipElRef}
        className="pointer-events-none fixed z-10 whitespace-nowrap rounded border border-[#00c8d2] bg-[#0d151b]/95 px-2.5 py-2 text-[11px] shadow-lg"
        style={{ display: tip && !menu ? 'block' : 'none' }}
      >
        {tip && (
          <>
            <div className="font-semibold text-[#00c8d2]">{tip.title}</div>
            {tip.lines.map((line, i) => (
              <div key={i} className="mt-0.5 text-[10px] text-[#7d95a3]">
                {line}
              </div>
            ))}
            {tip.ip && liveIp(tip.ip) !== tip.ip && (
              <div className="mt-0.5 text-[10px] text-[#ffb454]">
                now at {liveIp(tip.ip)}
              </div>
            )}
            {tip.ip ? (
              <div
                className={`mt-0.5 text-[10px] font-semibold ${STATUS_TEXT_CLASS[tipStatus]}`}
              >
                {STATUS_LABELS[tipStatus]}
              </div>
            ) : (
              <div className="mt-0.5 text-[10px] text-[#fc6a1e]">
                no device on file
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
